import type { OrderStatus } from '@fitting-room/contracts'
import { formatCents } from '../../../lib/orders'
import { BOUTIQUE_LOCALE } from '../../../lib/boutique'
import { uiStrings } from '../../../lib/strings'
import { OrderStatusBadge } from './OrderStatusBadge'

/** The part of an order ref these two need — the staff `VisitOrderRef` and the marketing view's both have it. */
export interface OrderAmount {
  readonly orderNo: string
  readonly status: OrderStatus
  readonly totalMinor: number
}

/** What the orders on an appointment come to, tax included, leaving out the canceled ones. */
export function liveOrdersTotalMinor(orders: readonly Pick<OrderAmount, 'status' | 'totalMinor'>[]): number {
  return orders.reduce((sum, order) => (order.status === 'canceled' ? sum : sum + order.totalMinor), 0)
}

/**
 * One order's amount, tax included — or Canceled in its place, because a
 * canceled order's total is not money anybody is owed.
 */
export function OrderAmountLabel({ order }: { readonly order: OrderAmount }) {
  if (order.status === 'canceled') return <OrderStatusBadge status="canceled" />
  return (
    <span className="tabular-nums" data-testid="order-amount">
      {formatCents(order.totalMinor)}
    </span>
  )
}

/**
 * The Total under an appointment's orders: only when there is more than one
 * to add up, or when one of them is canceled and the reader needs telling it
 * was left out.
 */
export function VisitOrdersTotal({ orders }: { readonly orders: readonly OrderAmount[] }) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const anyCanceled = orders.some((order) => order.status === 'canceled')
  if (orders.length < 2 && !anyCanceled) return null
  return (
    <p className="mb-2 flex items-baseline justify-between gap-3 text-sm" data-testid="visit-orders-total">
      <span>
        <span className="font-medium">{t.visitOrdersTotal}</span>
        {anyCanceled ? <span className="st-meta"> · {t.visitOrdersCanceledNotCounted}</span> : null}
      </span>
      <span className="font-medium tabular-nums">{formatCents(liveOrdersTotalMinor(orders))}</span>
    </p>
  )
}
